
interface DynamicFieldData {
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    personal_phone_number?: string;
    company_position?: string;
  };
  company?: {
    name?: string;
    email?: string;
    company_phone_number?: string;
    street_address?: string;
    suburb?: string;
    city?: string;
    province_state?: string;
    postal_code?: string;
    website?: string;
    // Bank account info
    bank_name?: string;
    branch_name?: string;
    branch_code?: string;
    account_number?: string;
    account_holder?: string;
  };
  customer?: {
    name?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    cell_numbers?: string[];
    street_address?: string;
    suburb?: string;
    city?: string;
    province_state?: string;
    postal_code?: string;
  };
  project?: {
    site_street_address?: string;
    site_suburb?: string;
    site_city?: string;
    site_province_state?: string;
    site_postal_code?: string;
  };
  estimate?: {
    site_street_address?: string;
    site_suburb?: string;
    site_city?: string;
    site_province_state?: string;
    site_postal_code?: string;
    estimate_number?: string;
    validity_days?: number;
    created_date?: string;
    expires_date?: string;
  };
  invoice?: {
    invoice_number?: string;
    due_date?: string;
    invoice_date?: string;
  };
  system?: {
    today_date?: string;
  };
}

// Format phone number to xxx xxx xxxx format
const formatPhoneNumber = (phone: string | undefined): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length >= 10) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
  }
  return phone;
};

export const processDynamicFields = (
  template: string,
  data: DynamicFieldData
): string => {
  if (!template) return '';
  
  let processedTemplate = template;
  
  // User fields
  if (data.user) {
    processedTemplate = processedTemplate
      .replace(/\{\{user_first_name\}\}/g, data.user.first_name || '')
      .replace(/\{\{user_last_name\}\}/g, data.user.last_name || '')
      .replace(/\{\{user_full_name\}\}/g, 
        [data.user.first_name, data.user.last_name].filter(Boolean).join(' '))
      .replace(/\{\{user_email\}\}/g, data.user.email || '')
      .replace(/\{\{user_phone\}\}/g, formatPhoneNumber(data.user.personal_phone_number) || '')
      .replace(/\{\{company_position\}\}/g, data.user.company_position || '')
      .replace(/\{\{company_role\}\}/g, data.user.company_position || '');
  }
  
  // Company fields
  if (data.company) {
    const fullAddress = [
      data.company.street_address,
      data.company.suburb,
      data.company.city,
      data.company.province_state,
      data.company.postal_code
    ].filter(Boolean).join(', ');
    
    processedTemplate = processedTemplate
      .replace(/\{\{company_name\}\}/g, data.company.name || '')
      .replace(/\{\{company_email\}\}/g, data.company.email || '')
      .replace(/\{\{company_phone\}\}/g, formatPhoneNumber(data.company.company_phone_number) || '')
      .replace(/\{\{company_address\}\}/g, fullAddress)
      .replace(/\{\{company_website\}\}/g, data.company.website || '')
      .replace(/\{\{company_bank_name\}\}/g, data.company.bank_name || '')
      .replace(/\{\{company_branch_name\}\}/g, data.company.branch_name || '')
      .replace(/\{\{company_branch_code\}\}/g, data.company.branch_code || '')
      .replace(/\{\{company_account_number\}\}/g, data.company.account_number || '')
      .replace(/\{\{company_account_holder\}\}/g, data.company.account_holder || '');
  }
  
  // Customer fields
  if (data.customer) {
    const customerAddress = [
      data.customer.street_address,
      data.customer.suburb,
      data.customer.city,
      data.customer.province_state,
      data.customer.postal_code
    ].filter(Boolean).join(', ');
    
    const customerPhone = data.customer.cell_numbers && data.customer.cell_numbers.length > 0 
      ? data.customer.cell_numbers[0] 
      : '';
    
    processedTemplate = processedTemplate
      .replace(/\{\{customer_name\}\}/g, data.customer.name || '')
      .replace(/\{\{customer_first_name\}\}/g, data.customer.first_name || '')
      .replace(/\{\{customer_last_name\}\}/g, data.customer.last_name || '')
      .replace(/\{\{customer_email\}\}/g, data.customer.email || '')
      .replace(/\{\{customer_phone\}\}/g, formatPhoneNumber(customerPhone))
      .replace(/\{\{customer_address\}\}/g, customerAddress);
  }
  
  // Project site address
  // Removed as a supported dynamic field; strip placeholder if it exists in legacy templates.
  processedTemplate = processedTemplate.replace(/\{\{project_site_address\}\}/g, '');

  
  // Estimate fields
  if (data.estimate) {
    const estimateSiteAddress = [
      data.estimate.site_street_address,
      data.estimate.site_suburb,
      data.estimate.site_city,
      data.estimate.site_province_state,
      data.estimate.site_postal_code
    ].filter(Boolean).join(', ');
    
    processedTemplate = processedTemplate
      .replace(/\{\{estimate_site_address\}\}/g, estimateSiteAddress)
      .replace(/\{\{estimate_number\}\}/g, data.estimate.estimate_number || '')
      .replace(/\{\{estimate_validity_days\}\}/g, data.estimate.validity_days?.toString() || '')
      .replace(/\{\{estimate_date\}\}/g, data.estimate.created_date || '')
      .replace(/\{\{estimate_created_date\}\}/g, data.estimate.created_date || '')
      .replace(/\{\{estimate_expires_date\}\}/g, data.estimate.expires_date || '');
  }
  
  // Invoice fields
  if (data.invoice) {
    processedTemplate = processedTemplate
      .replace(/\{\{invoice_number\}\}/g, data.invoice.invoice_number || '')
      .replace(/\{\{invoice_due_date\}\}/g, data.invoice.due_date || '')
      .replace(/\{\{invoice_date\}\}/g, data.invoice.invoice_date || '');
  }
  
  // System fields
  if (data.system) {
    processedTemplate = processedTemplate
      .replace(/\{\{today_date\}\}/g, data.system.today_date || '');
  }
  
  return processedTemplate;
};

export const getAvailableDynamicFields = () => {
  return {
    user: [
      { label: 'User First Name', value: '{{user_first_name}}' },
      { label: 'User Last Name', value: '{{user_last_name}}' },
      { label: 'User Full Name', value: '{{user_full_name}}' },
      { label: 'User Email', value: '{{user_email}}' },
      { label: 'User Phone', value: '{{user_phone}}' },
      { label: 'User Role/Position', value: '{{company_position}}' }
    ],
    company: [
      { label: 'Company Name', value: '{{company_name}}' },
      { label: 'Company Email', value: '{{company_email}}' },
      { label: 'Company Phone', value: '{{company_phone}}' },
      { label: 'Company Address', value: '{{company_address}}' },
      { label: 'Company Website', value: '{{company_website}}' },
      { label: 'Bank Name', value: '{{company_bank_name}}' },
      { label: 'Branch Name', value: '{{company_branch_name}}' },
      { label: 'Branch Code', value: '{{company_branch_code}}' },
      { label: 'Account Number', value: '{{company_account_number}}' },
      { label: 'Account Holder', value: '{{company_account_holder}}' }
    ],
    customer: [
      { label: 'Customer Full Name', value: '{{customer_name}}' },
      { label: 'Customer First Name', value: '{{customer_first_name}}' },
      { label: 'Customer Last Name', value: '{{customer_last_name}}' },
      { label: 'Customer Email', value: '{{customer_email}}' },
      { label: 'Customer Phone', value: '{{customer_phone}}' },
      { label: 'Customer Address', value: '{{customer_address}}' }
    ],
    // project dynamic fields removed

    estimate: [
      { label: 'Estimate Site Address', value: '{{estimate_site_address}}' },
      { label: 'Estimate Number', value: '{{estimate_number}}' },
      { label: 'Estimate Validity Days', value: '{{estimate_validity_days}}' },
      { label: 'Estimate Date', value: '{{estimate_date}}' },
      { label: 'Estimate Created Date', value: '{{estimate_created_date}}' },
      { label: 'Estimate Expires Date', value: '{{estimate_expires_date}}' }
    ],
    invoice: [
      { label: 'Invoice Number', value: '{{invoice_number}}' },
      { label: 'Invoice Due Date', value: '{{invoice_due_date}}' },
      { label: 'Invoice Date', value: '{{invoice_date}}' }
    ],
    system: [
      { label: "Today's Date", value: '{{today_date}}' }
    ]
  };
};
